/**
 * Owns the responsive layout: which view mode the page is in (full / compact /
 * mobile) and how big the canvas is in that mode. See docs/mobile.md.
 *
 * It resolves and keeps the five layout-only DOM refs (game container, main row,
 * center column, both sidebars) that App used to hold, and re-runs itself from a
 * ResizeObserver. App keeps the *policy* — when a resize is warranted (grid
 * change, modal close) — and calls {@link resize}.
 */
export type ViewMode = 'mobile' | 'compact' | 'full';

export interface LayoutContext {
    /** The game canvas — sized by this manager, owned by App. */
    canvas: HTMLElement;
    /** The grid-selector overlay, kept the same width as the canvas. */
    gridSelectorEl: HTMLElement;
    /** Resizing is skipped while a modal hides the canvas, to preserve its width. */
    isCanvasHidden: () => boolean;
    /** Called after every observer-driven resize (App re-renders the tutorial). */
    onResize: () => void;
}

export class LayoutManager {
    private ctx: LayoutContext;
    private gameContainer: HTMLElement;
    private mainRowEl: HTMLElement;
    private centerColumnEl: HTMLElement;
    private sidebarLeftEl: HTMLElement;
    private sidebarRightEl: HTMLElement;

    constructor(ctx: LayoutContext) {
        this.ctx = ctx;
        this.gameContainer = document.getElementById('game-container') as HTMLElement;
        this.mainRowEl = document.getElementById('main-row') as HTMLElement;
        this.centerColumnEl = document.getElementById('center-column') as HTMLElement;
        this.sidebarLeftEl = document.getElementById('sidebar-left') as HTMLElement;
        this.sidebarRightEl = document.getElementById('sidebar-right') as HTMLElement;
    }

    /** Observe the layout boxes and do the initial sizing pass. */
    start(): void {
        const resizeObserver = new ResizeObserver(() => {
            this.resize();
            this.ctx.onResize();
        });
        // Main row catches window resizes on desktop (the game container is
        // width-pinned there); the game container catches mobile resizes and
        // height changes.
        resizeObserver.observe(this.mainRowEl);
        resizeObserver.observe(this.gameContainer);

        // Initial size
        this.resize();
    }

    /** Resize the canvas and (re)pick the view mode for the current layout.
     *  Re-runnable on grid change, where the right-sidebar height can shift
     *  (Base → Vortex adds the vortex cluster) and overflow the chosen mode. */
    resize(): void {
        // Skip resize when canvas is hidden (modal mode) to preserve width
        if (this.ctx.isCanvasHidden()) return;

        const mode = this.computeViewMode();

        const style = getComputedStyle(this.gameContainer);
        const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

        let size: number;
        if (mode === 'mobile') {
            // Phone stack: the grid fills the column width. The container
            // height is driven by the canvas itself (grid row is content-sized),
            // so we can't measure leftover vertical space — cap against the
            // viewport so the grid never dominates a short screen.
            const availableWidth = this.gameContainer.clientWidth - paddingX;
            size = Math.min(availableWidth, window.innerHeight * 0.7);
            this.centerColumnEl.style.width = '';
        } else {
            // Desktop (full/compact): sidebars flank the grid, so the canvas is
            // the largest square that fits between them and the available height.
            const gap = parseFloat(getComputedStyle(this.mainRowEl).columnGap) || 0;
            const availableWidth = this.mainRowEl.clientWidth
                - this.sidebarLeftEl.offsetWidth
                - this.sidebarRightEl.offsetWidth
                - 2 * gap - paddingX;
            const availableHeight = this.gameContainer.clientHeight - paddingY;
            size = Math.min(availableWidth, availableHeight);
            // Pin the center column to the canvas width so the sidebars sit
            // right next to the grid (the whole row is centered via CSS).
            this.centerColumnEl.style.width = `${size + paddingX}px`;
        }

        this.ctx.canvas.style.width = `${size}px`;
        this.ctx.canvas.style.height = `${size}px`;
        this.ctx.gridSelectorEl.style.width = `${size}px`;
    }

    /**
     * Pick and apply the view mode by toggling `view-mobile` / `view-compact` on
     * <html> (the classes the responsive CSS keys off). The choice is
     * content-aware: a mode is only used if the right sidebar actually fits next
     * to the canvas.
     *   - portrait (height > width)      → mobile
     *   - full sidebar fits beside grid  → full   (no class)
     *   - compact sidebar fits           → compact
     *   - otherwise                      → mobile
     * `sidebar.scrollHeight <= sidebar.clientHeight` is exactly "the content fits
     * in the canvas-aligned region": the sidebar box spans the main row, and its
     * scrollHeight includes the canvas-top offset padding, so the comparison nets
     * out to content-height ≤ canvas-height. Each branch forces its layout before
     * measuring; the result is layout-deterministic, so it converges in at most
     * one extra resize pass without oscillating.
     */
    private computeViewMode(): ViewMode {
        const root = document.documentElement;

        if (window.matchMedia('(orientation: portrait)').matches) {
            root.classList.remove('view-compact');
            root.classList.add('view-mobile');
            return 'mobile';
        }

        // Landscape: prefer full, fall back to compact, else mobile.
        root.classList.remove('view-mobile', 'view-compact'); // measure full layout
        if (this.sidebarRightEl.scrollHeight <= this.sidebarRightEl.clientHeight) {
            return 'full';
        }

        root.classList.add('view-compact'); // measure compact layout
        if (this.sidebarRightEl.scrollHeight <= this.sidebarRightEl.clientHeight) {
            return 'compact';
        }

        root.classList.remove('view-compact');
        root.classList.add('view-mobile');
        return 'mobile';
    }
}
